import { useState } from "react"
import { router } from "@inertiajs/react"
import { Plus } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { StatusBadge } from "@/components/ui/status-badge"
import { CouponForm } from "@/features/staff/promotions/coupon-form"
import {
  PROMOTION_PATHS,
  couponTone,
  tPromotion,
  type Coupon,
} from "@/features/staff/promotions/promotions"
import { formatDateTime } from "@/lib/formatters"

type Props = {
  promotionId: number
  coupons: Coupon[]
  timeZone: string | null
}

type DialogState =
  | { mode: "revoke"; coupon: Coupon }
  | { mode: "destroy"; coupon: Coupon }
  | null

/**
 * The codes hanging off one rule.
 *
 * **Revoke and delete are different verbs and the row offers exactly one of
 * them.** `Location::Coupon` restricts on its redemptions, so a code somebody
 * already used cannot be destroyed — the ledger has to keep naming what that
 * diner actually got. `destroyable` is the server's answer to that question, not
 * a count this component re-derives, and it is what decides which control the
 * row renders. Showing both and letting the manager discover the refusal in a
 * flash message is how a screen teaches people to ignore flash messages.
 *
 * **A revocation asks for a reason.** It is optional at the endpoint, and it is
 * still asked for here: a dead code with no reason is a support ticket later,
 * and the field is the cheapest moment to answer it.
 *
 * The use counter is `tabular-nums` because it changes between renders, and the
 * expiry is rendered in the restaurant's zone rather than the browser's — a
 * manager in one city configuring a restaurant in another must not be shown a
 * different expiry than the till will enforce.
 */
export function CouponList({ promotionId, coupons, timeZone }: Props) {
  const [creating, setCreating] = useState(false)
  const [dialog, setDialog] = useState<DialogState>(null)
  const [reason, setReason] = useState("")

  const closeDialog = () => {
    setDialog(null)
    setReason("")
  }

  const confirmDialog = () => {
    if (!dialog) return

    if (dialog.mode === "revoke") {
      router.patch(
        PROMOTION_PATHS.revokeCoupon(promotionId, dialog.coupon.id),
        { revocation_reason: reason },
        { preserveScroll: true, onFinish: closeDialog }
      )
      return
    }

    router.delete(PROMOTION_PATHS.destroyCoupon(promotionId, dialog.coupon.id), {
      preserveScroll: true,
      onFinish: closeDialog,
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1">
          <p className="text-sm font-medium">{tPromotion("coupons_title")}</p>
          <p className="text-muted-foreground text-xs">{tPromotion("coupons_description")}</p>
        </div>
        {!creating && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setCreating(true)}
            data-testid={`promotion-${promotionId}-coupon-new`}
          >
            <Plus className="size-4" />
            {tPromotion("coupon_new")}
          </Button>
        )}
      </div>

      {creating && (
        <div className="border-border rounded-lg border px-4 py-4">
          <CouponForm promotionId={promotionId} onDone={() => setCreating(false)} />
        </div>
      )}

      {coupons.length === 0 ? (
        <p
          className="text-muted-foreground border-border rounded-lg border border-dashed px-4 py-3 text-sm"
          data-testid={`promotion-${promotionId}-coupons-empty`}
        >
          {tPromotion("coupons_empty")}
        </p>
      ) : (
        <ul className="divide-border divide-y" data-testid={`promotion-${promotionId}-coupons`}>
          {coupons.map((coupon) => (
            <li
              key={coupon.id}
              className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`coupon-row-${coupon.id}`}
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-semibold">{coupon.code}</span>
                  <StatusBadge
                    tone={couponTone(coupon.status)}
                    label={coupon.statusLabel}
                    data-testid={`coupon-status-${coupon.id}`}
                  />
                </div>
                <p className="text-muted-foreground text-xs">
                  <span className="tabular-nums" data-testid={`coupon-uses-${coupon.id}`}>
                    {tPromotion("coupon_uses", {
                      used: coupon.redemptionCount,
                      max: coupon.maxRedemptions,
                    })}
                  </span>
                  {" · "}
                  <span className="tabular-nums">
                    {coupon.expiresAt
                      ? tPromotion("coupon_expires_on", {
                          date: formatDateTime(coupon.expiresAt, {
                            timeZone: timeZone ?? undefined,
                          }),
                        })
                      : tPromotion("coupon_expires_never")}
                  </span>
                </p>
                {coupon.revocationReason && (
                  <p className="text-muted-foreground text-xs">
                    {tPromotion("coupon_revoked_reason", { reason: coupon.revocationReason })}
                  </p>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {coupon.status === "active" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setDialog({ mode: "revoke", coupon })}
                    data-testid={`coupon-revoke-${coupon.id}`}
                  >
                    {tPromotion("coupon_revoke")}
                  </Button>
                )}
                {coupon.destroyable ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setDialog({ mode: "destroy", coupon })}
                    data-testid={`coupon-delete-${coupon.id}`}
                  >
                    {tPromotion("coupon_delete")}
                  </Button>
                ) : (
                  <p className="text-muted-foreground max-w-56 text-xs">
                    {tPromotion("coupon_used_hint")}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog open={dialog !== null} onOpenChange={(next) => !next && closeDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dialog?.mode === "destroy"
                ? tPromotion("coupon_delete_title")
                : tPromotion("coupon_revoke_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dialog?.mode === "destroy"
                ? tPromotion("coupon_delete_description")
                : tPromotion("coupon_revoke_description")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {dialog?.mode === "revoke" && (
            <div className="space-y-2">
              <Label htmlFor="coupon-revoke-reason">
                {tPromotion("coupon_revoke_reason_label")}
              </Label>
              <Input
                id="coupon-revoke-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={tPromotion("coupon_revoke_reason_placeholder")}
                data-testid="coupon-revoke-reason"
              />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="coupon-dialog-cancel">
              {tPromotion("coupon_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDialog} data-testid="coupon-dialog-confirm">
              {dialog?.mode === "destroy"
                ? tPromotion("coupon_delete_confirm")
                : tPromotion("coupon_revoke_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
